import { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useUiStore } from '../store/uiStore';
import { useExperimentStore } from '../store/experimentStore';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { getLiquidColor } from './LiquidShader';

export default function FlaskModel() {
    const flaskGroup = useRef<THREE.Group>(null);
    const showMolecular = useUiStore((state) => state.showMolecular);
    const currentPH = useExperimentStore((state) => state.currentPH);
    const volumeAdded = useExperimentStore((state) => state.volumeAdded);
    const labStage = useExperimentStore((state) => state.labStage);
    const isFlaskFilled = useExperimentStore((state) => state.isFlaskFilled);
    const flaskVolume = useExperimentStore((state) => state.flaskVolume) || 25.0;

    // Swirl-on-click state
    const [hovered, setHovered] = useState(false);
    const [swirlActive, setSwirlActive] = useState(false);

    useEffect(() => {
        document.body.style.cursor = hovered ? 'pointer' : 'auto';
        return () => { document.body.style.cursor = 'auto'; };
    }, [hovered]);

    const handleClick = (e: any) => {
        e.stopPropagation();
        setSwirlActive(true);
        // Stop swirl after ~1.5 full rotations
        setTimeout(() => setSwirlActive(false), 900);
    };

    const liquidColor = getLiquidColor(currentPH);

    // === ACCURATE 250mL CORNING PYREX HEAVY-DUTY BEAKER DIMENSIONS ===
    const radius = 0.17;
    const beakerHeight = 0.40;
    const maxBeakerVolume = 300; // Visual height represents ~300mL slightly below brim

    // Fill animation state
    const [animFrac, setAnimFrac] = useState(0);
    const fillTimerRef = useRef(0);

    useEffect(() => {
        if (labStage === 'fill-flask') {
            fillTimerRef.current = 0;
            setAnimFrac(0);
        }
        if (labStage === 'titrate' || labStage === 'done') {
            setAnimFrac(1);
        }
    }, [labStage]);

    useFrame((_, dt) => {
        // Swirl animation
        if (swirlActive && flaskGroup.current) {
            flaskGroup.current.rotation.y += dt * 6;
        }
        if (labStage === 'fill-flask') {
            fillTimerRef.current += dt;
            if (fillTimerRef.current > 2.1) {
                setAnimFrac((prev) => Math.min(1, prev + dt * 0.5));
            }
        }
    });

    const getHeightForVolume = (vol: number) => {
        return (vol / maxBeakerVolume) * beakerHeight;
    };

    const currentVol = flaskVolume + volumeAdded;
    const targetBaseHeight = getHeightForVolume(flaskVolume);
    const targetTotalHeight = getHeightForVolume(currentVol);

    const showLiquid = !showMolecular && (isFlaskFilled || labStage === 'fill-flask' || labStage === 'titrate' || labStage === 'done');
    
    let totalLiquidHeight = 0;
    if (isFlaskFilled || labStage === 'titrate' || labStage === 'done') {
        totalLiquidHeight = targetTotalHeight;
    } else if (labStage === 'fill-flask') {
        totalLiquidHeight = targetBaseHeight * animFrac;
    }

    const liquidBottomY = -beakerHeight / 2 + 0.006;
    const liquidY = liquidBottomY + totalLiquidHeight / 2;

    // Graduation marks - major marks every 50mL (standard for 250mL beaker)
    const gradMarks = useMemo(() => {
        return [50, 100, 150, 200, 250].map((vol) => {
            const markHeight = getHeightForVolume(vol);
            const y = -beakerHeight / 2 + markHeight;
            return { vol, y, r: radius };
        });
    }, [radius, beakerHeight]);

    // Minor graduation marks - every 10mL (standard laboratory practice)
    const minorGradMarks = useMemo(() => {
        const marks: number[] = [];
        for (let vol = 10; vol <= 290; vol += 10) {
            if (vol % 50 !== 0) {
                marks.push(vol);
            }
        }
        return marks.map((vol) => {
            const markHeight = getHeightForVolume(vol);
            const y = -beakerHeight / 2 + markHeight;
            return { vol, y, r: radius };
        });
    }, [radius, beakerHeight]);

    // Micro graduation marks - every 5mL
    const microGradMarks = useMemo(() => {
        const marks: number[] = [];
        for (let vol = 5; vol <= 295; vol += 5) {
            // Skip major and minor marks
            if (vol % 10 !== 0) {
                marks.push(vol);
            }
        }
        return marks.map((vol) => {
            const markHeight = getHeightForVolume(vol);
            const y = -beakerHeight / 2 + markHeight;
            return { vol, y, r: radius };
        });
    }, [radius, beakerHeight]);

    // Opacity-based glass material
    const glassMaterial = (
        <meshPhysicalMaterial
            transparent={true}
            opacity={0.15}
            roughness={0.02}
            metalness={0.05}
            reflectivity={0.9}
            ior={1.47}
            clearcoat={1}
            clearcoatRoughness={0.02}
            color="#dcf0ff"   // faint lab glass tint
            side={THREE.DoubleSide}
            depthWrite={false}
        />
    );

    return (
        <group ref={flaskGroup} position={[0, -0.33, -0.2]}>

            {/* === LIQUID === */}
            {showLiquid && totalLiquidHeight > 0.01 && (
                <mesh position={[0, liquidY, 0]} renderOrder={0}>
                    <cylinderGeometry args={[radius - 0.008, radius - 0.008, totalLiquidHeight, 64]} />
                    <meshStandardMaterial
                        color={liquidColor}
                        transparent={true}
                        opacity={0.95}
                        roughness={0.1}
                        metalness={0}
                        depthWrite={true}
                        side={THREE.DoubleSide}
                    />
                </mesh>
            )}

            {/* Meniscus */}
            {showLiquid && totalLiquidHeight > 0.01 && (
                <mesh position={[0, liquidY + totalLiquidHeight / 2, 0]} renderOrder={0}>
                    <cylinderGeometry args={[radius - 0.006, radius - 0.008, 0.002, 48]} />
                    <meshStandardMaterial
                        color={liquidColor}
                        transparent={true}
                        opacity={0.8}
                        roughness={0.0}
                        depthWrite={false}
                        side={THREE.DoubleSide}
                    />
                </mesh>
            )}

            {/* === BEAKER GLASS (clickable) === */}
            <mesh
                position={[0, 0, 0]}
                castShadow
                renderOrder={1}
                onPointerOver={(e) => { e.stopPropagation(); setHovered(true); }}
                onPointerOut={() => setHovered(false)}
                onClick={handleClick}
            >
                <cylinderGeometry args={[radius, radius, beakerHeight, 64, 1, true]} />
                <meshPhysicalMaterial
                    transparent={true}
                    opacity={0.15}
                    roughness={0.02}
                    metalness={0.05}
                    reflectivity={0.9}
                    ior={1.47}
                    clearcoat={1}
                    clearcoatRoughness={0.02}
                    color="#dcf0ff"
                    side={THREE.DoubleSide}
                    depthWrite={false}
                    emissive={hovered ? '#6366f1' : '#000000'}
                    emissiveIntensity={hovered ? 0.15 : 0}
                />
            </mesh>



            {/* Top heavy-duty rim */}
            <mesh position={[0, beakerHeight / 2, 0]} rotation={[Math.PI / 2, 0, 0]} renderOrder={1}>
                <torusGeometry args={[radius + 0.006, 0.018, 12, 64]} />
                {glassMaterial}
            </mesh>



            {/* === MAJOR GRADUATION MARKS (50, 100, 150, 200, 250 mL) === */}
            {gradMarks.map(({ vol, y, r }) => (
                <group key={vol} position={[0, y, 0]}>
                    <mesh>
                        {/* Longer mark line on the front face */}
                        <cylinderGeometry args={[r + 0.001, r + 0.001, 0.002, 32, 1, true, -Math.PI / 4, Math.PI / 2]} />
                        <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} transparent opacity={0.95} />
                    </mesh>
                    <Text
                        position={[(r + 0.012) * Math.sin(Math.PI / 4), 0, (r + 0.012) * Math.cos(Math.PI / 4)]}
                        rotation={[0, 0, 0]}
                        fontSize={0.020}
                        color="#ffffff"
                        anchorX="left"
                        anchorY="middle"
                        fontWeight={600}
                    >
                        {vol}
                    </Text>
                </group>
            ))}

            {/* === MINOR GRADUATION MARKS (10, 20, 30, 40, 60, 70, etc - every 10mL) - MEDIUM === */}
            {minorGradMarks.map(({ vol, y, r }) => (
                <group key={`minor-${vol}`} position={[0, y, 0]}>
                    <mesh>
                        <cylinderGeometry args={[r + 0.001, r + 0.001, 0.0015, 32, 1, true, -Math.PI / 6, Math.PI / 3]} />
                        <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} transparent opacity={0.75} />
                    </mesh>
                </group>
            ))}

            {/* === MICRO GRADUATION MARKS (5, 15, 25, 35, etc - every 5mL) - SMALL === */}
            {microGradMarks.map(({ vol, y, r }) => (
                <group key={`micro-${vol}`} position={[0, y, 0]}>
                    <mesh>
                        {/* Shortest mark line */}
                        <cylinderGeometry args={[r + 0.001, r + 0.001, 0.0012, 32, 1, true, -Math.PI / 12, Math.PI / 6]} />
                        <meshBasicMaterial color="#ffffff" side={THREE.DoubleSide} transparent opacity={0.5} />
                    </mesh>
                </group>
            ))}

        </group>
    );
}
